// WorkspaceCoverService.js

// Firebase
import { deleteObject, ref, uploadBytes } from 'firebase/storage';

// Firebase
import { storage } from 'firebaseConfig';

// Services
import { prepareWorkspaceUploadIntent } from 'services/storage/StorageUploadIntentService';
import { assertWorkspaceUploadAllowed } from 'services/workspaces/WorkspaceUploadPolicyService';
import { updateWorkspace } from 'services/workspaces/WorkspaceWriteService';

const MAX_SOURCE_FILE_SIZE_BYTES = 20 * 1024 * 1024;
const MAX_ICON_OUTPUT_DIMENSION = 1024;
const MAX_COVER_OUTPUT_WIDTH = 1600;
const MAX_COVER_OUTPUT_HEIGHT = 900;
const JPEG_OUTPUT_MIME_TYPE = 'image/jpeg';
const JPEG_OUTPUT_QUALITY = 0.9;
const PNG_OUTPUT_MIME_TYPE = 'image/png';
const WEBP_OUTPUT_MIME_TYPE = 'image/webp';
const WEBP_OUTPUT_QUALITY = 0.9;

function getOutputSpec(file) {
    if (file?.type === PNG_OUTPUT_MIME_TYPE) {
        return {
            extension: 'png',
            mimeType: PNG_OUTPUT_MIME_TYPE,
            quality: undefined,
        };
    }

    if (file?.type === WEBP_OUTPUT_MIME_TYPE) {
        return {
            extension: 'webp',
            mimeType: WEBP_OUTPUT_MIME_TYPE,
            quality: WEBP_OUTPUT_QUALITY,
        };
    }

    return {
        extension: 'jpg',
        mimeType: JPEG_OUTPUT_MIME_TYPE,
        quality: JPEG_OUTPUT_QUALITY,
    };
}

function sanitizeFileStem(name) {
    const stem = String(name || 'cover')
        .replace(/\.[^/.]+$/, '')
        .toLowerCase()
        .replace(/[^a-z0-9-_]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, 60);
    return stem || 'cover';
}

function normalizeStoragePath(value) {
    return String(value || '').trim().replace(/^\/+/, '');
}

async function deleteStorageObjectIfPresent(storagePath) {
    const normalizedStoragePath = normalizeStoragePath(storagePath);
    if (!normalizedStoragePath) {
        return;
    }

    try {
        await deleteObject(ref(storage, normalizedStoragePath));
    } catch (error) {
        if (error?.code === 'storage/object-not-found') {
            return;
        }

        console.warn('WorkspaceCoverService: Failed to delete space cover object', error);
    }
}

function fileToImage(file) {
    return new Promise((resolve, reject) => {
        const objectUrl = URL.createObjectURL(file);
        const image = new Image();

        image.onload = () => {
            URL.revokeObjectURL(objectUrl);
            resolve(image);
        };

        image.onerror = () => {
            URL.revokeObjectURL(objectUrl);
            reject(new Error('Invalid image file.'));
        };

        image.src = objectUrl;
    });
}

function canvasToBlob(canvas, mimeType, quality) {
    return new Promise((resolve, reject) => {
        canvas.toBlob(
            (blob) => {
                if (blob) {
                    resolve(blob);
                    return;
                }
                reject(new Error('Failed to process image.'));
            },
            mimeType,
            quality
        );
    });
}

async function buildSquareCoverBlob(file) {
    if (!file || !file.type || !file.type.startsWith('image/')) {
        throw new Error('Please select an image file.');
    }

    if (file.size > MAX_SOURCE_FILE_SIZE_BYTES) {
        throw new Error('Image must be 20MB or smaller.');
    }

    const image = await fileToImage(file);

    const sourceSize = Math.min(image.naturalWidth, image.naturalHeight);
    if (!sourceSize || sourceSize <= 0) {
        throw new Error('Unable to read image dimensions.');
    }

    const cropX = Math.floor((image.naturalWidth - sourceSize) / 2);
    const cropY = Math.floor((image.naturalHeight - sourceSize) / 2);
    const targetSize = Math.min(sourceSize, MAX_ICON_OUTPUT_DIMENSION);

    const canvas = document.createElement('canvas');
    canvas.width = targetSize;
    canvas.height = targetSize;

    const context = canvas.getContext('2d');
    if (!context) {
        throw new Error('Failed to initialize image processor.');
    }

    context.imageSmoothingEnabled = true;
    context.imageSmoothingQuality = 'high';
    context.drawImage(
        image,
        cropX,
        cropY,
        sourceSize,
        sourceSize,
        0,
        0,
        targetSize,
        targetSize
    );

    const outputSpec = getOutputSpec(file);

    return {
        blob: await canvasToBlob(canvas, outputSpec.mimeType, outputSpec.quality),
        ...outputSpec,
    };
}

async function buildBannerCoverBlob(file) {
    if (!file || !file.type || !file.type.startsWith('image/')) {
        throw new Error('Please select an image file.');
    }

    if (file.size > MAX_SOURCE_FILE_SIZE_BYTES) {
        throw new Error('Image must be 20MB or smaller.');
    }

    const image = await fileToImage(file);
    const sourceWidth = image.naturalWidth;
    const sourceHeight = image.naturalHeight;

    if (!sourceWidth || sourceHeight <= 0) {
        throw new Error('Unable to read image dimensions.');
    }

    const scale = Math.min(
        1,
        MAX_COVER_OUTPUT_WIDTH / sourceWidth,
        MAX_COVER_OUTPUT_HEIGHT / sourceHeight
    );
    const targetWidth = Math.max(1, Math.round(sourceWidth * scale));
    const targetHeight = Math.max(1, Math.round(sourceHeight * scale));

    const canvas = document.createElement('canvas');
    canvas.width = targetWidth;
    canvas.height = targetHeight;

    const context = canvas.getContext('2d');
    if (!context) {
        throw new Error('Failed to initialize image processor.');
    }

    context.imageSmoothingEnabled = true;
    context.imageSmoothingQuality = 'high';
    context.drawImage(
        image,
        0,
        0,
        sourceWidth,
        sourceHeight,
        0,
        0,
        targetWidth,
        targetHeight
    );

    const outputSpec = getOutputSpec(file);

    return {
        blob: await canvasToBlob(canvas, outputSpec.mimeType, outputSpec.quality),
        ...outputSpec,
    };
}

async function uploadWorkspaceImage({
    workspaceKey,
    file,
    previousPath,
    processedBlob,
    extension,
    mimeType,
    fieldName,
    storageDirectory,
    uploadKind,
}) {
    const normalizedWorkspaceKey = String(workspaceKey || '').trim();
    if (!normalizedWorkspaceKey) {
        throw new Error('Workspace key is required.');
    }

    const normalizedPreviousPath = normalizeStoragePath(previousPath);

    await assertWorkspaceUploadAllowed({
        workspaceKey: normalizedWorkspaceKey,
        fileName: file?.name || storageDirectory,
        fileSizeBytes: processedBlob.size,
    });

    const fileStem = sanitizeFileStem(file?.name);
    const storagePath = `workspaces/${normalizedWorkspaceKey}/${storageDirectory}/${Date.now()}-${fileStem}.${extension}`;
    const uploadIntent = await prepareWorkspaceUploadIntent({
        workspaceKey: normalizedWorkspaceKey,
        storagePath,
        fileSizeBytes: processedBlob.size,
        contentType: mimeType,
        uploadKind,
    });
    const intentId = String(uploadIntent?.intentId || '').trim();

    const objectRef = ref(storage, storagePath);
    await uploadBytes(objectRef, processedBlob, {
        cacheControl: 'public, max-age=3600',
        contentType: mimeType,
        customMetadata: {
            intentId,
            workspaceKey: normalizedWorkspaceKey,
        },
    });

    await updateWorkspace(normalizedWorkspaceKey, {
        [fieldName]: storagePath,
    });

    if (normalizedPreviousPath && normalizedPreviousPath !== storagePath) {
        await deleteStorageObjectIfPresent(normalizedPreviousPath);
    }

    return storagePath;
}

async function clearWorkspaceImageField({ workspaceKey, previousPath, fieldName }) {
    const normalizedWorkspaceKey = String(workspaceKey || '').trim();
    if (!normalizedWorkspaceKey) {
        throw new Error('Workspace key is required.');
    }

    await updateWorkspace(normalizedWorkspaceKey, {
        [fieldName]: null,
    });

    await deleteStorageObjectIfPresent(normalizeStoragePath(previousPath));
}

export async function uploadWorkspaceIcon({ workspaceKey, file, previousIconPath = null }) {
    const {
        blob: processedBlob,
        extension,
        mimeType,
    } = await buildSquareCoverBlob(file);

    return uploadWorkspaceImage({
        workspaceKey,
        file,
        previousPath: previousIconPath,
        processedBlob,
        extension,
        mimeType,
        fieldName: 'iconPhotoUrl',
        storageDirectory: 'icon',
        uploadKind: 'workspace-icon',
    });
}

export async function uploadWorkspaceCover({ workspaceKey, file, previousCoverPath = null }) {
    const {
        blob: processedBlob,
        extension,
        mimeType,
    } = await buildBannerCoverBlob(file);

    return uploadWorkspaceImage({
        workspaceKey,
        file,
        previousPath: previousCoverPath,
        processedBlob,
        extension,
        mimeType,
        fieldName: 'sidebarCoverPhotoUrl',
        storageDirectory: 'sidebar-cover',
        uploadKind: 'workspace-sidebar-cover',
    });
}

export async function clearWorkspaceIcon({ workspaceKey, previousIconPath = null }) {
    await clearWorkspaceImageField({
        workspaceKey,
        previousPath: previousIconPath,
        fieldName: 'iconPhotoUrl',
    });
}

export async function clearWorkspaceCover({ workspaceKey, previousCoverPath = null }) {
    await clearWorkspaceImageField({
        workspaceKey,
        previousPath: previousCoverPath,
        fieldName: 'sidebarCoverPhotoUrl',
    });
}